/** Póster del Draw Round Robin (Eduardo 2026-10-09): una hoja DOBLE CARTA
 * (tabloide 11×17 in) horizontal por grupo, para pegar en la pared del club.
 * Matriz todos-contra-todos con marcador (desde el ganador) o fecha · hora ·
 * cancha, G-P y games de la tabla, rol de juegos por jornada, leyenda y hasta
 * 6 logos de patrocinadores. Solo arma HTML (texto escapado, imágenes por URL). */

import { dropMatchLabel, type DropMatchState } from "./dropMatch";
import { escapeHtml } from "./printTemplate";
import { scoreTextFromWinner } from "./scoreFromWinner";

/** Logo oficial de Torneos123 (Eduardo 2026-10-11: siempre el logo real,
 * nunca un texto armado). URL absoluta: la ventana de impresión no hereda la base. */
function torneos123LogoUrl(): string {
  const base = typeof window !== "undefined" ? window.location.origin : "";
  return `${base}${import.meta.env.BASE_URL}torneos123/logo-torneos123-header-on-light.png`;
}

export const POSTER_MAX_SPONSORS = 6;

export interface PosterPlayer {
  id: number;
  name: string;
  club?: string | null;
  dropped?: boolean;
}

export interface PosterMatch {
  id: number;
  roundNumber: number | null;
  status: string;
  scheduledDate: string | null;
  scheduledTime: string | null;
  court: string | null;
  player1Id: number;
  player2Id: number;
  winnerId: number | null;
  /** Marcador guardado desde el jugador 1. */
  scoreText: string | null;
  drop: DropMatchState | null;
  /** Fechas de la jornada («del sáb 3 al dom 4 de oct») si no hay calendario. */
  jornadaRange?: string | null;
  /** Lo mismo en corto para la celda («25 nov – 2 dic»). */
  jornadaShort?: string | null;
}

export interface PosterStanding {
  wins: number;
  losses: number;
  gamesWon: number;
  gamesLost: number;
}

export interface PosterGroup {
  name: string;
  players: PosterPlayer[];
  matches: PosterMatch[];
  standings: Map<number, PosterStanding>;
}

export interface DrawPosterInput {
  tournamentName: string;
  categoryName: string;
  formatLabel?: string | null;
  venue?: string | null;
  ball?: string | null;
  qualifyText?: string | null;
  organizerLogoUrl?: string | null;
  tournamentLogoUrl?: string | null;
  sponsorLogoUrls?: ReadonlyArray<string | null | undefined>;
  updatedAt?: Date;
  groups: PosterGroup[];
}

const WEEKDAYS = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];
const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const PLAYED = new Set(["completed", "retired"]);
const WALKOVER = new Set(["walkover", "defaulted", "no_show", "double_walkover"]);

/** Default (DEF, Eduardo 2026-10-13): con marcador se jugó hasta el default
 * y se pinta como un retiro (marcador y quién ganó); sin marcador, como un
 * W.O. pero diciendo «DEF». */
function isPlayedMatch(match: PosterMatch): boolean {
  return PLAYED.has(match.status) || (match.status === "defaulted" && /\d/.test(match.scoreText ?? ""));
}

function isOffMatch(match: PosterMatch): boolean {
  return match.status === "cancelled" || (WALKOVER.has(match.status) && !isPlayedMatch(match)) || Boolean(match.drop);
}

function parseDate(value: string | null): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? "");
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12) : null;
}

/** «2026-10-03» → «sáb 3 oct». */
export function posterDay(value: string | null): string {
  const d = parseDate(value);
  return d ? `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}` : "";
}

/** «09:00:00» → «9:00». */
export function posterTime(value: string | null): string {
  const m = /^(\d{1,2}):(\d{2})/.exec(value ?? "");
  return m ? `${Number(m[1])}:${m[2]}` : "";
}

function courtLong(court: string | null): string {
  const c = (court ?? "").trim();
  return /^\d+$/.test(c) ? `Cancha ${c}` : c;
}

function courtShort(court: string | null): string {
  const c = (court ?? "").trim();
  return /^\d+$/.test(c) ? `C${c}` : c;
}

export type PosterCellKind = "self" | "won" | "lost" | "pending" | "off" | "empty";

function offLabel(match: PosterMatch): [string, string] {
  if (match.drop) {
    const label = dropMatchLabel(match.drop);
    if (match.drop.kind === "cancelled") return ["Cancelado", "por baja"];
    if (match.drop.kind === "walkover") return ["W.O.", "por baja"];
    return [label, ""];
  }
  if (match.status === "cancelled") return ["Cancelado", ""];
  if (match.status === "defaulted") return ["DEF", ""];
  if (match.status === "double_walkover") return ["Doble W.O.", ""];
  return ["W.O.", ""];
}

function scoreFor(match: PosterMatch): string {
  const winnerIsP1 = match.winnerId == null ? null : match.winnerId === match.player1Id;
  return scoreTextFromWinner(match.scoreText, winnerIsP1);
}

function whenLine(match: PosterMatch, short: boolean): string {
  const parts = short
    ? [posterDay(match.scheduledDate).split(" ").slice(0, 2).join(" "), posterTime(match.scheduledTime), courtShort(match.court)]
    : [posterTime(match.scheduledTime), courtLong(match.court)];
  return parts.filter(Boolean).join(" · ");
}

export function posterCell(
  rowId: number,
  colId: number,
  match: PosterMatch | undefined,
): { kind: PosterCellKind; html: string; title?: string } {
  if (rowId === colId) return { kind: "self", html: BALL_SVG };
  if (!match) return { kind: "empty", html: "" };
  if (isOffMatch(match)) {
    const [title, sub] = offLabel(match);
    return { kind: "off", html: `<strong>${escapeHtml(title)}</strong>${sub ? `<small>${escapeHtml(sub)}</small>` : ""}` };
  }
  if (isPlayedMatch(match) && match.winnerId != null) {
    const won = match.winnerId === rowId;
    const score = scoreFor(match) || (won ? "Ganó" : "Perdió");
    const when = whenLine(match, true);
    return {
      kind: won ? "won" : "lost",
      // Cada set en una pieza: el renglón solo se corta entre sets.
      html: `<strong class="dp-score">${score
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => `<span class="dp-set">${escapeHtml(part)}</span>`)
        .join(" ")}</strong>${when ? `<small>${escapeHtml(when)}</small>` : ""}`,
    };
  }
  const day = posterDay(match.scheduledDate);
  if (day) {
    const rest = whenLine(match, false);
    return { kind: "pending", html: `<strong>${escapeHtml(day)}</strong>${rest ? `<small>${escapeHtml(rest)}</small>` : ""}` };
  }
  // Sin calendario (Eduardo 2026-10-20): solo el rango corto de la jornada
  // («25 nov – 2 dic») o un guion; el texto completo queda en el title.
  const title = ["Por programar", match.jornadaRange].filter(Boolean).join(" ");
  return {
    kind: "pending",
    html: match.jornadaShort
      ? `<span class="dp-range">${escapeHtml(match.jornadaShort)}</span>`
      : '<span class="dp-dash">—</span>',
    title,
  };
}

const BALL_SVG =
  '<svg class="dp-ball" viewBox="0 0 28 28" aria-hidden="true"><circle cx="14" cy="14" r="12" fill="#d8f14e" stroke="#8aa61a" stroke-width="2"/><path d="M5 8c5 3 5 9 0 12M23 8c-5 3-5 9 0 12" fill="none" stroke="#ffffff" stroke-width="2"/></svg>';

function pairKey(a: number, b: number): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

function rolEntry(match: PosterMatch, numberById: Map<number, number>): { text: string; muted: boolean } {
  const a = numberById.get(match.player1Id) ?? "?";
  const b = numberById.get(match.player2Id) ?? "?";
  const head = `${a} vs ${b}`;
  if (isOffMatch(match)) {
    return { text: `${head} · ${offLabel(match)[0].toLowerCase()}`, muted: true };
  }
  if (isPlayedMatch(match) && match.winnerId != null) {
    return { text: `${head} · ${scoreFor(match)}`.trim(), muted: false };
  }
  const when = [WEEKDAYS[parseDate(match.scheduledDate)?.getDay() ?? -1] ?? "", posterTime(match.scheduledTime), courtShort(match.court)]
    .filter(Boolean)
    .join(" ");
  return { text: `${head} · ${when || "por programar"}`, muted: false };
}

function groupPage(group: PosterGroup, header: string, extras: string): string {
  const players = group.players;
  const numberById = new Map(players.map((p, i) => [p.id, i + 1]));
  const byPair = new Map<string, PosterMatch>();
  // El vigente gana sobre un cancelado de la misma pareja.
  for (const m of group.matches) {
    const key = pairKey(m.player1Id, m.player2Id);
    const prev = byPair.get(key);
    if (!prev || prev.status === "cancelled") byPair.set(key, m);
  }
  const hasClub = players.some((p) => (p.club ?? "").trim());
  const n = players.length;
  const rowH = Math.max(46, Math.min(118, Math.floor(520 / Math.max(n, 1))));
  // Anchos y letra que bajan con el número de jugadores (hasta 12 sin
  // desbordar): ancho útil ≈ 1460 px en la hoja doble carta.
  const nameW = n <= 6 ? 21 : n <= 9 ? 17 : 14;
  const clubW = hasClub ? (n <= 6 ? 14 : 9) : 0;
  const statW = n <= 8 ? 6.5 : 5.5;
  const spacing = n <= 8 ? 7 : 5;
  const cellW = (1460 * (100 - nameW - clubW - 3 * statW)) / 100 / Math.max(n, 1) - spacing;
  const cellFs = Math.max(10, Math.min(17, Math.floor(cellW / 5)));
  const style = `--dp-name-w:${nameW}%;--dp-club-w:${clubW}%;--dp-stat-w:${statW}%;--dp-sp:${spacing}px;--dp-fs:${cellFs}px;--dp-name-fs:${n <= 8 ? 20 : 16}px`;

  const head = `<tr><th class="dp-th-left">Jugador</th>${hasClub ? '<th class="dp-th-left dp-th-club">Club</th>' : ""}${players
    .map((_, i) => `<th><span class="dp-num">${i + 1}</span></th>`)
    .join("")}<th>G-P</th><th>Games</th><th>Lugar</th></tr>`;

  const rows = players
    .map((p, i) => {
      const st = group.standings.get(p.id);
      const cells = players
        .map((q) => {
          const cell = posterCell(p.id, q.id, byPair.get(pairKey(p.id, q.id)));
          return `<td class="dp-cell dp-cell--${cell.kind}"${cell.title ? ` title="${escapeHtml(cell.title)}"` : ""}>${cell.html}</td>`;
        })
        .join("");
      const gp = st ? `${st.wins}-${st.losses}` : "—";
      const games = st ? `${st.gamesWon}-${st.gamesLost}` : "—";
      return `<tr style="height:${rowH}px" class="${p.dropped ? "dp-row--dropped" : ""}"><td class="dp-player"><span class="dp-num">${i + 1}</span><strong class="dp-name">${escapeHtml(p.name)}</strong></td>${
        hasClub ? `<td class="dp-club">${escapeHtml(p.club ?? "")}</td>` : ""
      }${cells}<td class="dp-stat dp-stat--gp">${gp}</td><td class="dp-stat">${games}</td><td class="dp-place"></td></tr>`;
    })
    .join("");

  const rounds = new Map<number, PosterMatch[]>();
  for (const m of group.matches) {
    const r = m.roundNumber ?? 0;
    rounds.set(r, [...(rounds.get(r) ?? []), m]);
  }
  const roundNumbers = [...rounds.keys()].sort((a, b) => a - b);
  const current = roundNumbers.find((r) =>
    (rounds.get(r) ?? []).some((m) => !PLAYED.has(m.status) && !WALKOVER.has(m.status) && m.status !== "cancelled"),
  );
  const rol = roundNumbers
    .map((r) => {
      const items = (rounds.get(r) ?? [])
        .map((m) => rolEntry(m, numberById))
        .map((e) => `<span${e.muted ? ' class="dp-muted"' : ""}>${escapeHtml(e.text)}</span>`)
        .join("");
      const label = r > 0 ? `Jornada ${r}` : "Sin jornada";
      return `<div class="dp-rol-col"><span class="dp-pill${r === current ? " dp-pill--current" : ""}">${label}</span>${items}</div>`;
    })
    .join("");

  return `<section class="dp-page">
${header.replace("__GROUP__", escapeHtml(group.name))}
<section class="dp-card dp-matrix"><table style="${style}">${head}${rows}</table></section>
<section class="dp-bottom">
  <div class="dp-card dp-rol"><span class="dp-eyebrow">Rol de juegos</span><div class="dp-rol-grid" style="grid-template-columns:repeat(${Math.max(1, roundNumbers.length <= 8 ? roundNumbers.length : Math.ceil(roundNumbers.length / 2))},minmax(0,1fr))">${rol}</div></div>
  ${extras}
</section>
</section>`;
}

export function buildDrawPosterHtml(input: DrawPosterInput): string {
  const updated = new Intl.DateTimeFormat("es-MX", {
    timeZone: "America/Mexico_City",
    dateStyle: "medium",
    timeStyle: "short",
  }).format(input.updatedAt ?? new Date());
  const info = [input.formatLabel, input.venue ? `Sede ${input.venue}` : null, input.ball ? `Pelota ${input.ball}` : null, input.qualifyText ? `Pasan a playoffs: ${input.qualifyText}` : null]
    .filter((x) => x && String(x).trim())
    .map((x) => escapeHtml(String(x)))
    .join(" · ");
  const logo = (url: string | null | undefined, alt: string) =>
    url ? `<span class="dp-logo"><img src="${escapeHtml(url)}" alt="${alt}" /></span>` : "<span></span>";
  const header = `<header class="dp-header">${logo(input.organizerLogoUrl, "Organizador")}<div class="dp-title"><span class="dp-tour">${escapeHtml(
    input.tournamentName,
  )}</span><div class="dp-title-row"><span class="dp-group">__GROUP__</span><span class="dp-cat">${escapeHtml(input.categoryName)}</span></div>${
    info ? `<span class="dp-info">${info}</span>` : ""
  }</div>${logo(input.tournamentLogoUrl, "Torneo")}</header>`;

  const legend = `<div class="dp-card dp-legend">
<span><i class="dp-sw dp-sw--won"></i> Ganó · marcador desde el ganador</span>
<span><i class="dp-sw dp-sw--lost"></i> Perdió</span>
<span><i class="dp-sw dp-sw--pending"></i> Por jugar · día · hora · cancha</span>
<span><i class="dp-sw dp-sw--off"></i> Cancelado / W.O.</span>
<span class="dp-updated">Actualizado ${escapeHtml(updated)}</span>
</div>`;

  const sponsors = (input.sponsorLogoUrls ?? []).filter((u): u is string => Boolean(u && u.trim())).slice(0, POSTER_MAX_SPONSORS);
  const footer = `<footer class="dp-footer">${
    sponsors.length
      ? `<div class="dp-sponsors">${sponsors.map((u) => `<span class="dp-sponsor"><img src="${escapeHtml(u)}" alt="Patrocinador" /></span>`).join("")}</div>`
      : "<div></div>"
  }<div class="dp-brand"><img src="${escapeHtml(torneos123LogoUrl())}" alt="Torneos123 · Compite. Progresa. Gana." /></div></footer>`;

  const pages = input.groups
    .filter((g) => g.players.length > 0)
    .map((g) => groupPage(g, header, legend).replace(/<\/section>\s*$/, `${footer}</section>`))
    .join("\n");

  return `<!doctype html>
<html lang="es"><head><meta charset="utf-8" />
<title>${escapeHtml(`Póster del Draw · ${input.categoryName} · ${input.tournamentName}`)}</title>
<style>${POSTER_CSS}</style><style id="dp-paper">${PAPER_CSS.tabloid}</style></head>
<body>
<div class="dp-toolbar"><span class="dp-paper-label">Hoja:</span><button type="button" data-paper="tabloid" aria-pressed="true">Doble carta</button><button type="button" data-paper="letter" aria-pressed="false">Carta</button><button type="button" onclick="window.close()">Regresar</button><button type="button" onclick="window.print()">Imprimir / PDF</button></div>
${pages || '<p class="dp-empty">Esta categoría todavía no tiene grupos con jugadores.</p>'}
<script>${PAPER_SCRIPT}</script>
</body></html>`;
}

/** Tamaño de hoja (Eduardo 2026-10-20): doble carta por omisión; en carta
 * horizontal la hoja completa se reduce para que el grupo quepa en una. */
const PAPER_CSS = {
  tabloid: "@page { size: 17in 11in; margin: 0.4in; }",
  letter: "@page { size: 11in 8.5in; margin: 0.3in; } @media print { .dp-page { zoom: 0.64; } }",
} as const;

/* Si el grupo no cabe en la hoja (muchos jugadores, nombres largos), la
 * matriz y el rol se reducen juntos hasta que todo entra: nada se corta. */
const PAPER_SCRIPT = `(function(){function fit(){document.querySelectorAll(".dp-page").forEach(function(p){
var parts=p.querySelectorAll(".dp-matrix,.dp-bottom");var z=1;parts.forEach(function(e){e.style.zoom="";});
while(p.scrollHeight>p.clientHeight+1&&z>0.4){z-=0.02;parts.forEach(function(e){e.style.zoom=String(z);});}});}
fit();window.addEventListener("load",fit);window.addEventListener("beforeprint",fit);if(document.fonts){document.fonts.ready.then(fit);}})();
(function(){var css=${JSON.stringify(PAPER_CSS)};var el=document.getElementById("dp-paper");
document.querySelectorAll("[data-paper]").forEach(function(b){b.addEventListener("click",function(){el.textContent=css[b.getAttribute("data-paper")];
document.querySelectorAll("[data-paper]").forEach(function(o){o.setAttribute("aria-pressed",String(o===b));});});});})();`;

const POSTER_CSS = `
* { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
html, body { margin: 0; background: #f4f7fb; color: #101b2d; font-family: "Satoshi", "Inter", -apple-system, "Segoe UI", Arial, sans-serif; }
.dp-toolbar { position: sticky; top: 0; z-index: 5; display: flex; justify-content: flex-end; gap: 8px; padding: 8px; background: rgba(255,255,255,.94); }
.dp-paper-label { align-self: center; font-weight: 800; color: #42536b; }
.dp-toolbar button[aria-pressed="true"] { background: #164a7d; color: #fff; border-color: #164a7d; }
.dp-toolbar button { border: 1px solid #c9d3df; border-radius: 999px; padding: 6px 14px; background: #fff; font: inherit; font-weight: 800; cursor: pointer; }
.dp-page { width: 16.2in; height: 10.2in; margin: 0 auto 24px; padding: 26px 30px; background: #f4f7fb; display: flex; flex-direction: column; gap: 18px; overflow: hidden; page-break-after: always; break-after: page; }
.dp-page:last-of-type { page-break-after: auto; break-after: auto; }
.dp-header { background: #164a7d; color: #fff; border-radius: 28px; padding: 20px 28px; display: grid; grid-template-columns: 170px 1fr 170px; align-items: center; gap: 24px; }
.dp-logo { height: 90px; border-radius: 18px; background: #fff; display: flex; align-items: center; justify-content: center; padding: 8px; }
.dp-logo img { max-width: 100%; max-height: 74px; object-fit: contain; }
.dp-title { display: flex; flex-direction: column; gap: 8px; }
.dp-tour { font-size: 17px; font-weight: 800; letter-spacing: .16em; text-transform: uppercase; color: #d8f14e; }
.dp-title-row { display: flex; align-items: baseline; gap: 18px; flex-wrap: wrap; }
.dp-group { font-size: 48px; font-weight: 800; line-height: 1; }
.dp-cat { padding: 6px 18px; border-radius: 999px; background: #d8f14e; color: #101b2d; font-size: 21px; font-weight: 800; }
.dp-info { font-size: 17px; color: #c9d6e6; }
.dp-card { background: #fff; border-radius: 24px; border: 1px solid rgba(16,38,64,.10); }
.dp-matrix { padding: 14px; }
.dp-matrix table { width: 100%; border-collapse: separate; border-spacing: var(--dp-sp, 7px); table-layout: fixed; font-size: var(--dp-fs, 17px); }
.dp-matrix th { font-size: 14px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; color: #5b6b84; text-align: center; }
.dp-matrix th.dp-th-left { text-align: left; padding: 0 8px; width: var(--dp-name-w, 21%); }
.dp-matrix th.dp-th-club { width: var(--dp-club-w, 14%); }
.dp-matrix th:nth-last-child(-n+3) { width: var(--dp-stat-w, 6.5%); }
.dp-num { display: inline-block; min-width: 30px; height: 30px; line-height: 30px; border-radius: 999px; background: #164a7d; color: #fff; font-weight: 800; text-align: center; font-size: 16px; }
.dp-player { padding: 0 10px; border-radius: 16px; background: #f4f7fb; overflow-wrap: anywhere; }
.dp-player .dp-num { margin-right: 8px; }
.dp-name { font-size: var(--dp-name-fs, 20px); display: inline-block; vertical-align: middle; max-width: calc(100% - 42px); }
.dp-club { padding: 0 8px; color: #42536b; overflow-wrap: anywhere; }
.dp-row--dropped .dp-player, .dp-row--dropped .dp-club, .dp-row--dropped .dp-stat { color: #8a95a6; }
.dp-row--dropped .dp-name { text-decoration: line-through; }
.dp-row--dropped .dp-player .dp-num { background: #b8c3d3; }
.dp-cell { border-radius: 16px; text-align: center; padding: 4px 4px; line-height: 1.2; overflow-wrap: break-word; }
.dp-cell strong { display: block; }
.dp-cell small { display: block; font-size: 0.78em; }
.dp-cell--self { background: repeating-linear-gradient(45deg, #f1f4f8 0 8px, #ffffff 8px 16px); }
.dp-ball { width: min(40px, 2.3em); height: min(40px, 2.3em); }
.dp-cell--won { background: #164a7d; color: #fff; }
.dp-cell--won small { color: #c9d6e6; }
.dp-cell--lost { background: #e7eff9; color: #42536b; }
.dp-cell--lost .dp-score { color: #164a7d; }
.dp-score { font-size: 1.2em; }
.dp-set { white-space: nowrap; }
.dp-cell--pending { background: #fff; border: 2px dashed #8aa61a; }
.dp-cell--pending small { color: #42536b; font-size: 0.85em; }
.dp-range { display: block; font-weight: 800; color: #42536b; font-size: 0.9em; }
.dp-dash { color: #b8c3d3; font-weight: 700; }
.dp-cell--off { background: #eef1f6; color: #5b6b84; }
.dp-cell--empty { background: #f9fafc; }
.dp-stat { text-align: center; font-weight: 700; }
.dp-stat--gp { font-weight: 800; font-size: 20px; }
.dp-place { border-radius: 16px; border: 2px solid #c9d3df; }
.dp-bottom { display: grid; grid-template-columns: 1fr 380px; gap: 16px; }
.dp-rol, .dp-legend { padding: 16px 20px; display: flex; flex-direction: column; gap: 10px; }
.dp-eyebrow { font-size: 15px; font-weight: 800; letter-spacing: .14em; text-transform: uppercase; color: #164a7d; }
.dp-rol-grid { display: grid; gap: 12px; font-size: 16px; }
.dp-rol-col { display: flex; flex-direction: column; gap: 5px; }
.dp-pill { align-self: flex-start; padding: 4px 14px; border-radius: 999px; background: #e7eff9; color: #164a7d; font-weight: 800; }
.dp-pill--current { background: #d8f14e; color: #101b2d; }
.dp-muted { color: #8a95a6; }
.dp-legend { font-size: 15px; color: #42536b; }
.dp-sw { display: inline-block; width: 18px; height: 18px; border-radius: 5px; vertical-align: middle; }
.dp-sw--won { background: #164a7d; }
.dp-sw--lost { background: #e7eff9; }
.dp-sw--pending { border: 2px dashed #8aa61a; }
.dp-sw--off { background: #eef1f6; }
.dp-updated { color: #5b6b84; }
.dp-footer { margin-top: auto; display: flex; align-items: center; gap: 18px; }
.dp-footer > div:first-child { flex: 1; }
.dp-sponsors { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 14px; }
.dp-sponsor { height: 64px; border-radius: 16px; background: #fff; border: 1px solid rgba(16,38,64,.10); display: flex; align-items: center; justify-content: center; padding: 6px; }
.dp-sponsor img { max-width: 100%; max-height: 52px; object-fit: contain; }
.dp-brand { display: flex; align-items: center; justify-content: flex-end; }
.dp-brand img { height: 56px; width: auto; display: block; }
.dp-empty { padding: 40px; font-size: 20px; }
@media print { .dp-toolbar { display: none !important; } .dp-page { margin: 0; } html, body { background: #f4f7fb; } }
`;

/** Abre la ventana YA (dentro del clic, para que no la bloqueen) y escribe
 * el póster cuando llegan los datos. */
export function openDrawPosterWindow(): { write: (html: string) => void; fail: (message: string) => void } {
  const win = window.open("", "_blank", "width=1400,height=950");
  if (!win) {
    throw new Error("El navegador bloqueó la ventana de impresión. Permite pop-ups para Torneos123 y vuelve a intentar.");
  }
  win.document.write('<!doctype html><meta charset="utf-8"><title>Preparando póster…</title><p style="font-family:sans-serif;padding:24px">Preparando póster…</p>');
  return {
    write: (html) => {
      win.document.open();
      win.document.write(html);
      win.document.close();
    },
    fail: (message) => {
      win.document.open();
      win.document.write(`<!doctype html><meta charset="utf-8"><p style="font-family:sans-serif;padding:24px">${escapeHtml(message)}</p>`);
      win.document.close();
    },
  };
}
